import { Box, Typography } from "@mui/material";
import { Layout, authService, getAppConfig } from "framework-react-core";
import { useCallback, useMemo, useEffect, useRef } from "react";
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router-dom";

export function ErrorRoot() {
  const error = useRouteError();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  const isAuthError = useMemo(() => {
    if (!isRouteErrorResponse(error)) {
      return false;
    }
    const errorData = error.data as
      | { message?: unknown; redirect?: unknown }
      | undefined;
    const hasSignInMessage =
      typeof errorData?.message === "string" &&
      errorData.message.toLowerCase().includes("sign in");
    const hasRedirectPayload = typeof errorData?.redirect === "string";
    return hasSignInMessage || hasRedirectPayload;
  }, [error]);

  const handleSignIn = useCallback(() => {
    const scopes = getAppConfig().getConfigValue<{ default: string[] }>(
      "FRAMEWORK_UI_AUTH_SCOPES",
    ).default;
    const redirectPath =
      isRouteErrorResponse(error) && typeof error.data?.redirect === "string"
        ? error.data.redirect
        : null;
    if (redirectPath) {
      sessionStorage.setItem("post_login_redirect", redirectPath);
    }
    const redirectStartPage = redirectPath
      ? `${window.location.origin}${redirectPath}`
      : undefined;
    authService?.msalClient.loginRedirect({ scopes, redirectStartPage });
  }, [error]);

  useEffect(() => {
    if (
      isRouteErrorResponse(error) &&
      typeof error.data?.redirect === "string"
    ) {
      sessionStorage.setItem("post_login_redirect", error.data.redirect);
    }
  }, [error]);

  useEffect(() => {
    if (!isAuthError || !authService) return;
    // Avoid loops if AAD/MSAL bounced back with an error in the URL
    if (window.location.search.includes("error=")) return;
    if (sessionStorage.getItem("sso_redirect_in_progress") === "1") return;
    sessionStorage.setItem("sso_redirect_in_progress", "1");
    handleSignIn();
  }, [isAuthError, handleSignIn]);

  useEffect(() => {
    if (isAuthError || !error) return;

    console.error("Route error caught by ErrorRoot:", error);

    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    navigate("/service-unavailable?code=503", { replace: true });
  }, [error, isAuthError, navigate]);

  const msg = useMemo(() => {
    if (isRouteErrorResponse(error)) {
      if (typeof error.data?.message === "string") {
        return error.data.message;
      }
      if (typeof error.statusText === "string" && error.statusText.length > 0) {
        return error.statusText;
      }
    }
    return "Please sign in to access this page.";
  }, [error, isAuthError]);

  if (!isAuthError) {
    return null;
  }

  return (
    <Layout title="Rules Engine" menuOptions={[]}>
      <Box textAlign="center" p={10}>
        <Typography variant="h4" gutterBottom>
          {isAuthError && (
            <span
              onClick={handleSignIn}
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Sign In
            </span>
          )}{" "}
          {msg}
        </Typography>
      </Box>
    </Layout>
  );
}
